import { useEffect, useMemo, useState } from "react"

import { WithID } from "shared/types/WithID"

interface UseStableSortedOrderOptions {
  /**
   * If true, items that are removed from the input array will be kept in the result
   * but marked with an `isRemoved` property. This is useful for animations or
   * showing "deleted" states before fully removing items.
   * @default false
   */
  keepRemoved?: boolean
}

type StableSortedItem<T> = T & {
  /**
   * Indicates if this item was removed from the input array but kept due to keepRemoved option.
   * Only present when keepRemoved is true.
   */
  isRemoved?: boolean
}

/**
 * useStableSortedOrder
 *
 * Maintains a stable sort order for a list of items throughout the component lifecycle.
 * The initial order is determined by the sort function, and this order is preserved
 * even when the input array changes.
 *
 * **Behavior:**
 * - On mount: Items are sorted once using the provided sort function
 * - New items: Appended to the end in arrival order
 * - Removed items: Filtered out (or marked as removed if keepRemoved is true)
 * - Existing items: Maintain their original sorted position
 *
 * **Use cases:**
 * - Lists where you want consistent positioning (e.g., team members, notifications)
 * - Preventing jarring reorders when data updates
 * - Maintaining visual stability during real-time updates
 *
 * @template ItemType - Type of items in the array (must extend WithID)
 *
 * @param config - Configuration object
 * @param config.items - Array of items, each must have a unique `id` property
 * @param config.sortFn - Sort function for initial ordering. Defaults to no sorting (insertion order)
 * @param options - Additional options
 * @param options.keepRemoved - If true, removed items are marked but not filtered out
 *
 * @returns Array of items in stable sorted order, with optional `isRemoved` property
 *
 * @example
 * ```typescript
 * // Basic usage - stable alphabetical sort
 * const sortedMembers = useStableSortedOrder({
 *   items: teamMembers,
 *   sortFn: (a, b) => a.name.localeCompare(b.name)
 * })
 *
 * // With removed items kept for animation
 * const membersWithRemoved = useStableSortedOrder({
 *   items: teamMembers,
 *   sortFn: (a, b) => a.name.localeCompare(b.name)
 * }, { keepRemoved: true })
 *
 * // Filter out removed items in render
 * membersWithRemoved.filter(member => !member.isRemoved)
 * ```
 */
export function useStableSortedOrder<ItemType extends WithID>(
  {
    items,
    sortFn = () => 0
  }: {
    items: ItemType[] | null | undefined
    sortFn?: (a: ItemType, b: ItemType) => number
  },
  options: UseStableSortedOrderOptions = {}
): StableSortedItem<ItemType>[] {
  const { keepRemoved = false } = options

  const [idOrder, setIdOrder] = useState<string[]>(
    [...(items || [])].sort(sortFn).map(item => item.id)
  )
  const [allItems, setAllItems] = useState<Map<string, ItemType>>(
    new Map(items?.map(item => [item.id, item]) || [])
  )

  useEffect(() => {
    setAllItems(prev => {
      const newItems = new Map(prev)
      items?.forEach(item => {
        newItems.set(item.id, item)
      })
      return newItems
    })
  }, [items])

  useEffect(() => {
    const currentIds = new Set(items?.map(i => i.id))

    setIdOrder(prevOrder => {
      const stillExisting = keepRemoved
        ? prevOrder
        : prevOrder.filter(id => currentIds.has(id))

      const prevSet = new Set(stillExisting)
      const newIds = items?.map(i => i.id).filter(id => !prevSet.has(id)) || []

      return [...stillExisting, ...newIds]
    })
  }, [items, keepRemoved])

  const orderedItems = useMemo(() => {
    const result: StableSortedItem<ItemType>[] = []
    const currentItemsMap = new Map(items?.map(item => [item.id, item]) || [])

    // eslint-disable-next-line no-restricted-syntax
    for (const id of idOrder) {
      const currentItem = allItems.get(id)

      if (currentItem && !currentItemsMap.has(id)) {
        result.push({
          ...currentItem,
          isRemoved: true
        })
      } else if (currentItem) {
        result.push(currentItem)
      }
    }

    return result
  }, [items, idOrder, allItems, keepRemoved])

  return orderedItems
}

export type { StableSortedItem }
